export const ARTICLE_TABLE_BLOT_NAME = 'article-table';
export const ARTICLE_TABLE_CLASS_NAME = 'article-table';
export const ARTICLE_TABLE_JSON_ATTRIBUTE = 'data-table-json';
export const ARTICLE_TABLE_ROWS_ATTRIBUTE = 'data-table-rows';
export const ARTICLE_TABLE_COLS_ATTRIBUTE = 'data-table-cols';
export const ARTICLE_TABLE_HEADER_ROWS_ATTRIBUTE = 'data-table-header-rows';
export const ARTICLE_TABLE_VERSION_ATTRIBUTE = 'data-table-version';
export const ARTICLE_TABLE_LABEL_ATTRIBUTE = 'data-table-label';
export const ARTICLE_TABLE_TEXT_FALLBACK_CLASS_NAME = 'article-table__text-fallback';

function clampInteger(value, minimum = 0, fallback = 0) {
    const parsed = Number.parseInt(value, 10);
    if (!Number.isFinite(parsed)) {
        return fallback;
    }

    return Math.max(minimum, parsed);
}

export function sanitizeArticleTableCellText(value) {
    if (typeof value !== 'string') {
        value = value === null || value === undefined ? '' : String(value);
    }

    return value
        .replace(/\r\n?/g, '\n')
        .replace(/\u0000/g, '');
}

function normalizeArticleTableRows(rows) {
    const rawRows = Array.isArray(rows) ? rows : [];
    const normalizedRows = rawRows.map((row) => {
        const rawCells = Array.isArray(row) ? row : [row];
        return rawCells.map((cell) => sanitizeArticleTableCellText(cell));
    });

    const rowCount = Math.max(1, normalizedRows.length);
    const columnCount = Math.max(1, normalizedRows.reduce((maxColumns, row) => Math.max(maxColumns, row.length), 0));

    while (normalizedRows.length < rowCount) {
        normalizedRows.push([]);
    }

    return normalizedRows.map((row) => {
        const normalizedRow = row.slice(0, columnCount);
        while (normalizedRow.length < columnCount) {
            normalizedRow.push('');
        }

        return normalizedRow;
    });
}

export function buildArticleTableLabel(value) {
    const rowLabel = value.rowCount === 1 ? 'row' : 'rows';
    const columnLabel = value.columnCount === 1 ? 'column' : 'columns';
    return `Table - ${value.columnCount} ${columnLabel} x ${value.rowCount} ${rowLabel}`;
}

export function buildArticleTablePlainText(value) {
    return value.rows
        .map((row) => row.join(' | '))
        .join('\n');
}

export function encodeArticleTablePayload(value) {
    const normalizedValue = normalizeArticleTableValue(value);

    return encodeURIComponent(JSON.stringify({
        version: normalizedValue.version,
        headerRows: normalizedValue.headerRows,
        rows: normalizedValue.rows,
    }));
}

export function decodeArticleTablePayload(encodedPayload) {
    if (typeof encodedPayload !== 'string' || encodedPayload.trim() === '') {
        return null;
    }

    const candidates = [encodedPayload];
    try {
        candidates.unshift(decodeURIComponent(encodedPayload));
    }
    catch (error) {}

    for (const candidate of candidates) {
        try {
            return JSON.parse(candidate);
        }
        catch (error) {}
    }

    return null;
}

export function normalizeArticleTableValue(value) {
    let payload = value;

    if (typeof value === 'string') {
        payload = decodeArticleTablePayload(value) || {};
    }
    else if (value && typeof value === 'object' && typeof value.encodedJson === 'string') {
        payload = decodeArticleTablePayload(value.encodedJson) || value;
    }
    else if (!value || typeof value !== 'object') {
        payload = {};
    }

    const rows = normalizeArticleTableRows(payload.rows);
    const rowCount = rows.length;
    const columnCount = rows[0] ? rows[0].length : 1;
    const version = clampInteger(payload.version, 1, 1);
    const headerRows = Math.min(rowCount, clampInteger(payload.headerRows, 0, 0));

    const normalizedValue = {
        version,
        headerRows,
        rows,
        rowCount,
        columnCount,
    };

    return {
        ...normalizedValue,
        label: buildArticleTableLabel(normalizedValue),
        plainText: buildArticleTablePlainText(normalizedValue),
        encodedJson: encodeURIComponent(JSON.stringify({
            version: normalizedValue.version,
            headerRows: normalizedValue.headerRows,
            rows: normalizedValue.rows,
        })),
    };
}

export function readArticleTableNodeValue(node) {
    if (!node || typeof node.getAttribute !== 'function') {
        return normalizeArticleTableValue(null);
    }

    const encodedPayload = node.getAttribute(ARTICLE_TABLE_JSON_ATTRIBUTE) || '';
    const decodedPayload = decodeArticleTablePayload(encodedPayload);

    if (decodedPayload) {
        return normalizeArticleTableValue(decodedPayload);
    }

    const rowCount = clampInteger(node.getAttribute(ARTICLE_TABLE_ROWS_ATTRIBUTE), 1, 1);
    const columnCount = clampInteger(node.getAttribute(ARTICLE_TABLE_COLS_ATTRIBUTE), 1, 1);
    const headerRows = Math.min(rowCount, clampInteger(node.getAttribute(ARTICLE_TABLE_HEADER_ROWS_ATTRIBUTE), 0, 0));

    return normalizeArticleTableValue({
        version: clampInteger(node.getAttribute(ARTICLE_TABLE_VERSION_ATTRIBUTE), 1, 1),
        headerRows,
        rows: Array.from({ length: rowCount }, () => Array.from({ length: columnCount }, () => '')),
    });
}

function upsertArticleTableTextFallback(node, normalizedValue) {
    if (!node || typeof node.querySelector !== 'function') {
        return;
    }

    let fallbackNode = node.querySelector(`.${ARTICLE_TABLE_TEXT_FALLBACK_CLASS_NAME}`);
    if (!fallbackNode) {
        fallbackNode = node.ownerDocument.createElement('div');
        fallbackNode.className = ARTICLE_TABLE_TEXT_FALLBACK_CLASS_NAME;
        fallbackNode.setAttribute('hidden', 'hidden');
        fallbackNode.setAttribute('aria-hidden', 'true');
        node.appendChild(fallbackNode);
    }

    fallbackNode.textContent = normalizedValue.plainText;
}

export function writeArticleTableNodeAttributes(node, value) {
    if (!node || typeof node.setAttribute !== 'function') {
        return normalizeArticleTableValue(value);
    }

    const normalizedValue = normalizeArticleTableValue(value);

    node.setAttribute(ARTICLE_TABLE_JSON_ATTRIBUTE, normalizedValue.encodedJson);
    node.setAttribute(ARTICLE_TABLE_ROWS_ATTRIBUTE, String(normalizedValue.rowCount));
    node.setAttribute(ARTICLE_TABLE_COLS_ATTRIBUTE, String(normalizedValue.columnCount));
    node.setAttribute(ARTICLE_TABLE_HEADER_ROWS_ATTRIBUTE, String(normalizedValue.headerRows));
    node.setAttribute(ARTICLE_TABLE_VERSION_ATTRIBUTE, String(normalizedValue.version));
    node.setAttribute(ARTICLE_TABLE_LABEL_ATTRIBUTE, normalizedValue.label);
    upsertArticleTableTextFallback(node, normalizedValue);

    return normalizedValue;
}
